export type Subscriber<State> = (s: State) => void;

export type UnsubscribeCallback = () => void;

export enum FetchStatus {
  Idle,
  Loading,
  Success,
  Error,
}

type StoreState<State> = State & {fetchStatus: FetchStatus; errorFetchData: unknown | null};

export class Store<State> {
  private readonly listeners = new Set<Subscriber<StoreState<State>>>();

  protected state: StoreState<State>;
  protected initialState: StoreState<State>;

  constructor(initialState: State) {
    this.state = {...initialState, fetchStatus: FetchStatus.Idle, errorFetchData: null};
    this.initialState = {...initialState, fetchStatus: FetchStatus.Idle, errorFetchData: null};
  }

  public resetState = (): void => {
    this.setState(this.initialState);
  };

  public getState = (): StoreState<State> => {
    return this.state;
  };

  public setState = (state: State): State => {
    this.state = {...state, fetchStatus: this.state.fetchStatus, errorFetchData: this.state.errorFetchData};

    this.notify();

    return this.state;
  };

  public setValue = <K extends keyof StoreState<State>>(key: K, value: StoreState<State>[K]): void => {
    this.state[key] = value;
    this.state = {...this.state, [key]: value};
    this.notify();
  };

  public subscribe = (listener: Subscriber<StoreState<State>>): UnsubscribeCallback => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  public unsubscribe = (listener: Subscriber<StoreState<State>>): void => {
    this.listeners.delete(listener);
  };

  // fetch status
  public isIdleFetchStatus(): boolean {
    return this.state.fetchStatus === FetchStatus.Idle;
  }

  public isLoadingFetchStatus(): boolean {
    return this.state.fetchStatus === FetchStatus.Loading;
  }

  public isSuccessFetchStatus(): boolean {
    return this.state.fetchStatus === FetchStatus.Success;
  }

  public isErrorFetchStatus(): boolean {
    return this.state.fetchStatus === FetchStatus.Error;
  }

  public setIdleFetchStatus(): void {
    this.setFetchStatus(FetchStatus.Idle);
  }

  public setLoadingFetchStatus(): void {
    this.setFetchStatus(FetchStatus.Loading);
  }

  public setSuccessFetchStatus(): void {
    this.setFetchStatus(FetchStatus.Success);
  }

  public setErrorFetchStatus(): void {
    this.setFetchStatus(FetchStatus.Error);
  }

  private setFetchStatus = (status: FetchStatus): void => {
    this.state = {...this.state, fetchStatus: status};
    this.notify();
  };

  // error fetch status
  public setErrorFetchData = (error: unknown): void => {
    this.state = {...this.state, errorFetchData: error};
  };

  public clearErrorFetchData = (): void => {
    this.state = {...this.state, errorFetchData: null};
  };

  public getErrorFetchData = (): unknown => {
    return this.getState().errorFetchData;
  };

  protected notify(): void {
    this.listeners.forEach((listener) => {
      listener(this.state);
    });
  }
}
